import React from 'react';
import {Typography} from '@mui/material';
import {t} from 'i18next';

import {ReactComponent as Connector} from '../../../assests/svg/connector.svg';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {fieldTypes} from '../model';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
interface ChildComponentProps {
  availableFields: any;
  isLoading?: boolean;
}
interface Field {
  id: string;
  analyzer: string;
  fileType?: string; // Optional if needed
  name?: string;
  fieldType?: string;
  isSearchable?: boolean;
  fieldLabel: string;
}

const groupColors = [
  {background: '#00AFB8', text: '#15181C'},
  {background: '#4555D6', text: '#FFFFFF'},
  {background: '#D9E277', text: '#15181C'},
  {background: '#89A6FF', text: '#15181C'},
  {background: '#FFD6A5', text: '#15181C'},
  {background: '#D6C4F1', text: '#15181C'},
  {background: '#A5E9D0', text: '#15181C'},
  {background: '#FF9E2C', text: '#15181C'},
  {background: '#B290E5', text: '#15181C'},
  {background: '#FFCCCC', text: '#15181C'},
  {background: '#55D5A7', text: '#15181C'},
  {background: '#764CB6', text: '#FFFFFF'},
  {background: '#C9043F', text: '#FFFFFF'},
  {background: '#04A269', text: '#FFFFFF'},
  {background: '#B8BBC0', text: '#15181C'},
] as const;

const MetaDataFields: React.FC<ChildComponentProps> = ({
  availableFields,
  isLoading = false,
}) => {
  if (isLoading) {
    return <AurLoadingIndicator />;
  }

  const standardMetaData: Field[] = availableFields.length
    ? availableFields.filter((data: Field) => data.fieldType === fieldTypes.standard)
    : [];

  let groupedByStandardAnalyzer: Record<string, Field[]> = {};

  if (standardMetaData.length > 0) {
    groupedByStandardAnalyzer = standardMetaData.reduce(
      (acc: Record<string, Field[]>, field: Field) => {
        if (!acc[field.analyzer]) {
          acc[field.analyzer] = [];
        }
        acc[field.analyzer].push(field);
        return acc;
      },
      {} as Record<string, Field[]>
    );

    // Step 2: Sort each group alphabetically by name
    Object.keys(groupedByStandardAnalyzer).forEach((analyzer) => {
      groupedByStandardAnalyzer[analyzer].sort((a: Field, b: Field) => {
        const nameA = a.name ?? '';
        const nameB = b.name ?? '';
        return nameA.localeCompare(nameB);
      });
    });
  }

  const customMetaData: Field[] = availableFields.length
    ? availableFields.filter((data: Field) => data.fieldType === fieldTypes.custom)
    : [];
  let groupedByCustomAnalyzer: Record<string, Field[]> = {};

  if (customMetaData.length > 0) {
    groupedByCustomAnalyzer = customMetaData.reduce(
      (acc: Record<string, Field[]>, field: Field) => {
        if (!acc[field.analyzer]) {
          acc[field.analyzer] = [];
        }
        acc[field.analyzer].push(field);
        return acc;
      },
      {} as Record<string, Field[]>
    );

    // Step 2: Sort each group alphabetically by name
    Object.keys(groupedByCustomAnalyzer).forEach((analyzer) => {
      groupedByCustomAnalyzer[analyzer].sort((a: Field, b: Field) => {
        const nameA = a.name ?? '';
        const nameB = b.name ?? '';
        return nameA.localeCompare(nameB);
      });
    });
  }

  const getDisplayName = (analyzer: string): string => {
    switch (analyzer) {
      case 'eca_url_email':
        return t('analyzers.eca_url_email');
      case 'eca_path_hierarchy':
        return t('analyzers.eca_path_hierarchy');
      case 'eca_whitespace':
        return t('analyzers.eca_whitespace');
      case 'cjk_analyzer':
        return t('analyzers.cjk_analyzer');
      default:
        // Convert snake_case or camelCase to Title Case
        return (
          analyzer
            // eslint-disable-next-line no-useless-escape
            .replace(/[_\-]+/g, ' ')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(
              /\w\S*/g,
              (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
            )
        );
    }
  };

  const indexDetails = useSelector(
    (state: RootState) => state.indexes.indexData
  );
  return (
    <>
      <div className="metadata-header">
        <Typography className="typo-text headline3_size text metadata-title">
          {t(`indexes.viewSettings.metaField`)}
        </Typography>
        <Typography className="typo-text body1_size meta-desc">
          {t(`indexes.viewSettings.metaFieldDesc`)}
        </Typography>
      </div>
      {indexDetails?.ele?.type === 'Custom' && (
        <div className="custom-fields-container">
          <Typography className="typo-text subhead4_size text custom-fields-header ml-140">
            {t(`indexes.viewSettings.customFields`)}{' '}
          </Typography>
          {Object.keys(groupedByCustomAnalyzer).map((analyzer, index) => {
            const {background: groupColor, text: groupTextColor} =
              groupColors[index % groupColors.length]; // Cycle through the colors
            return (
              <div key={analyzer} style={{marginBottom: '16px'}}>
                <ul className="fields-list ml-140">
                  {groupedByCustomAnalyzer[analyzer].map((field: Field) => {
                    return (
                      <li
                        key={field.id}
                        className="field-item"
                        aria-label={`${field.fieldLabel} ${getDisplayName(field.analyzer)}`}
                      >
                        <Typography
                          className="typo-text body2_size field-name"
                          gutterBottom
                          aria-hidden="true"
                        >
                          {field.fieldLabel}
                        </Typography>
                        <Connector
                          className="field-connector"
                          aria-hidden={true}
                        />
                        <Typography
                          className="field-tag tag-0 typo-text body3_size"
                          style={{
                            backgroundColor: groupColor,
                            color: groupTextColor,
                          }} // Apply the group color to the tag
                          aria-hidden="true"
                        >
                          {getDisplayName(field.analyzer)}
                        </Typography>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}
      <div className="custom-fields-container">
        <Typography className="typo-text subhead4_size text custom-fields-header ml-140">
          {t(`indexes.viewSettings.standardField`)}{' '}
        </Typography>
        {Object.keys(groupedByStandardAnalyzer).map((analyzer, index) => {
          const {background: groupColor, text: groupTextColor} =
            groupColors[index % groupColors.length]; // Cycle through the colors
          return (
            <div key={analyzer} style={{marginBottom: '16px'}}>
              <ul className="fields-list two-column-layout ml-140">
                {groupedByStandardAnalyzer[analyzer].map((field: Field) => {
                  return (
                    <li
                      key={field.id}
                      className="field-item"
                      aria-label={`${field.fieldLabel} ${getDisplayName(field.analyzer)}`}
                    >
                      <Typography
                        className="typo-text body2_size field-name"
                        gutterBottom
                        aria-hidden="true"
                      >
                        {field.fieldLabel}
                      </Typography>
                      <Connector
                        className="field-connector"
                        aria-hidden="true"
                      />
                      <Typography
                        className="field-tag tag-0 typo-text body3_size"
                        style={{
                          backgroundColor: groupColor,
                          color: groupTextColor,
                        }} // Apply the group color to the tag
                        aria-hidden="true"
                      >
                        {getDisplayName(field.analyzer)}
                      </Typography>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default MetaDataFields;
